// spokeGrowthColormap
//
// Small sequential colormaps tuned for the spoke-growth animation.
// Marker fill color is sampled by cumulative count (1..max) so a
// spoke that has referred once shows the coldest color and a
// heavy repeat sender lights up with the hottest.
//
// Colormaps are intentionally short (5–7 stops) so the animation
// reads at a glance rather than requiring color-chart study.

// Interpolate between two hex colors ('#rrggbb'). t in [0,1].
export function lerpHex(a, b, t) {
  const ar = parseInt(a.slice(1, 3), 16);
  const ag = parseInt(a.slice(3, 5), 16);
  const ab = parseInt(a.slice(5, 7), 16);
  const br = parseInt(b.slice(1, 3), 16);
  const bg = parseInt(b.slice(3, 5), 16);
  const bb = parseInt(b.slice(5, 7), 16);
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(bl)}`;
}

// Walk a stops array as a piecewise-linear color scale.
function sampleStops(stops, t01) {
  const t = Math.max(0, Math.min(1, t01));
  if (stops.length === 1) return stops[0];
  const seg = t * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(seg));
  const local = seg - i;
  return lerpHex(stops[i], stops[i + 1], local);
}

// Bundled colormaps, all sequential (low -> high).
export const COLORMAPS = [
  {
    id: 'ylorrd',
    name: 'Yellow → Orange → Red',
    stops: ['#ffffcc', '#ffeda0', '#fed976', '#feb24c', '#fd8d3c', '#fc4e2a', '#bd0026'],
  },
  {
    id: 'viridis',
    name: 'Viridis',
    stops: ['#440154', '#443983', '#31688e', '#21918c', '#35b779', '#8fd744', '#fde725'],
  },
  {
    id: 'plasma',
    name: 'Plasma',
    stops: ['#0d0887', '#5c01a6', '#9c179e', '#cc4778', '#ed7953', '#fdb32f', '#f0f921'],
  },
  {
    id: 'blues',
    name: 'Cool Blues',
    stops: ['#f7fbff', '#deebf7', '#c6dbef', '#9ecae1', '#6baed6', '#3182bd', '#08519c'],
  },
  {
    id: 'teal-magenta',
    name: 'Teal → Magenta',
    stops: ['#0d9488', '#22d3ee', '#a5f3fc', '#f5d0fe', '#c026d3', '#701a75'],
  },
  {
    // Classic "jet"/thermal heatmap: cool blues → cyan → green → yellow → red.
    id: 'heatmap',
    name: 'Heatmap',
    stops: [
      '#00008b', // deep blue
      '#0000ff', // blue
      '#00bfff', // deep sky / cyan-blue
      '#00ffff', // cyan
      '#00ff7f', // spring green
      '#7fff00', // chartreuse
      '#ffff00', // yellow
      '#ff7f00', // orange
      '#ff0000', // red
      '#b30000', // deep red
    ],
  },
];

export const DEFAULT_COLORMAP_ID = 'ylorrd';

// Power < 1 lifts mid-volume spokes out of the cool band. Referral
// counts are almost always long-tailed (one hub partner with thousands,
// dozens with tens), so a linear count/max mapping left ~everything
// deep blue even when circles looked "large". ~0.4 puts a spoke at
// 10% of the max count near the middle of the spectrum.
const INTENSITY_POWER = 0.4;

/**
 * Shared 0..1 intensity used for BOTH marker color and marker size so
 * they always tell the same story: least = small + cold, most = large + hot.
 */
export function countIntensity(count, max) {
  if (!count || count <= 0) return 0;
  const safeMax = Math.max(1, max || 1);
  if (count >= safeMax) return 1;
  return Math.pow(count / safeMax, INTENSITY_POWER);
}

/**
 * Marker radius (canvas / icon scale units) from the same intensity as color.
 */
export function spokeMarkerSize(count, max, { minSize = 7, maxSize = 28 } = {}) {
  const t = countIntensity(count, max);
  return minSize + (maxSize - minSize) * t;
}

/**
 * Sample a colormap by count. `max` is the maximum cumulative count among
 * currently-visible spokes (the "hot"/red end of the scale).
 */
export function sampleColormap(colormapId, count, max) {
  const cm = COLORMAPS.find(c => c.id === colormapId) || COLORMAPS[0];
  if (!count || count <= 0) return cm.stops[0];
  return sampleStops(cm.stops, countIntensity(count, max));
}
